import { Component, DestroyRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { EraPecEccAquaticEu, EraPecEccAquaticDataTransaction, EraPecEccAquaticGeography, EraPecEccAquaticUs } from 'src/app/environmental-risk-assessment/era-pec-ecc/era-pec-ecc-aquatic/era-pec-ecc-aquatic.model';
import { Project } from 'src/app/shared/models/project';
import { Constants } from 'src/app/shared/utils/constants';
import { ERAPecEccAquaticLogicService } from './services/era-pec-ecc-aquatic.logic.service';
import { GridComponent } from 'src/app/shared/components/grid/grid.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TabMenuLogicService } from 'src/app/shared/services/tab-menu.logic.service';
import { ColDef, RowSelectedEvent } from 'ag-grid-community';
import { ERAConstants } from '../../environmental-risk-assessment.constants';

@Component({
  selector: 'app-era-pec-ecc-aquatic',
  templateUrl: './era-pec-ecc-aquatic.component.html',
  styleUrls: ['./era-pec-ecc-aquatic.component.css']
})
export class ERAPecEccAquaticComponent implements OnInit {

  @Input() isProjectOwnershipValid: boolean = false;
  @Input() menuService!: TabMenuLogicService;
  @Input() selectedProject?: Project;
  @Input({required: true}) type!: string;
  @Input({required: true}) columnDefs!: ColDef;
  @Input({required: true}) geography!: EraPecEccAquaticGeography;

  @ViewChild('grid') grid!: GridComponent;

  public rowData: EraPecEccAquaticEu[] | EraPecEccAquaticUs[] = [];

  private destroyRef = inject(DestroyRef);
  private pksRowsToDelete: number[] = [];
  private saveRef?: string;

  private logicService = inject(ERAPecEccAquaticLogicService);

  public async ngOnInit(): Promise<void> {
    this.initSubscribes();
    this.getSavedData();
  }

  initSubscribes(){
    this.menuService.activeIndexChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.saveRef = this.menuService.saveRef;
      this.save()
    });
  }

  private async getSavedData(): Promise<void> {

    let data: EraPecEccAquaticEu[] | EraPecEccAquaticUs[] = [];

    if(this.geography.geographyName === Constants.GEOGRAPHYS.EUROPE_UNION)
      data = await this.logicService.getPecEccAquaticEuByProjectPk(this.selectedProject!.projectPk);

    if(this.geography.geographyName === Constants.GEOGRAPHYS.USA){

      switch(this.type){
        case Constants.PEC_ECC_TYPES.AQUATIC:
          data = await this.logicService.getPecEccAquaticUsByProjectPk(this.selectedProject!.projectPk, [Constants.WATERBODIES_USA.POND, Constants.WATERBODIES_USA.RESERVOIR]);
          break;
        case Constants.PEC_ECC_TYPES.TPEZ:
          data = await this.logicService.getPecEccAquaticUsByProjectPk(this.selectedProject!.projectPk, [Constants.WATERBODIES_USA.TERRESTRIAL]);
          break;
        case Constants.PEC_ECC_TYPES.WPEZ:
          data = await this.logicService.getPecEccAquaticUsByProjectPk(this.selectedProject!.projectPk, [Constants.WATERBODIES_USA.WETLAND]);
          break;
      }

    }

    this.setData(data);
    this.refreshSelectedValuesTimeout();
  }

  private setData( data: EraPecEccAquaticEu[] | EraPecEccAquaticUs[] ){
    data.forEach(x => x.modelName = this.logicService.modelsDropdown.find(model => model.optionValue == x.modelPk)?.optionLabel as string);
    this.rowData = data.sort((a: EraPecEccAquaticEu | EraPecEccAquaticUs, b: EraPecEccAquaticEu | EraPecEccAquaticUs) => a.runPk! - b.runPk!);
  }

  public onDropdownChanged(event: any): void {

    switch(event.field){
      case 'activeIngredientPk':
        this.grid.gridApi.setColumnDefs(this.columnDefs);
        break;
      case 'modelPk':
        event.row.scenarioFocus = null;
        this.resetValues(event);
        this.addDefaultScenarioValue(event);
        this.grid.gridApi.setColumnDefs(this.columnDefs);
        break;
      case 'scenarioFocus':
        this.resetValues(event);
        this.grid.gridApi.setColumnDefs(this.columnDefs);
        break;
    }
    this.grid.gridApi.redrawRows();
  }

  resetValues(event: any){
    event.row.scenarios = null;
    event.row.waterbody = null;
    event.row.bufferDistance = null;
    event.row.nozzleDriftReduction = null;
    event.row.region = null;
    event.row.period = null;
    event.row.bbchEarliest = null;
  }

  addDefaultScenarioValue(event: any){
    const modelName = this.logicService.modelsDropdown.find((x: any) => x.optionValue == event.value)?.optionLabel;
    switch(modelName){
      case Constants.MODELS.SWASH:
        event.row.scenarioFocus = 'Step 3';
        break;
      case Constants.MODELS.SWAN:
        event.row.scenarioFocus = 'Step 4';
        break;
    }
  }

  private save(): void {

    if( !this.grid?.transactionList && this.pksRowsToDelete.length === 0 ) {
      this.menuService.setSuccess(true);
      return;
    }
    const { geographyName } = this.geography;
    const pecEccAquatics = this.logicService.transformGridDataToSaveFormat(this.grid.transactionList, geographyName, this.selectedProject!.projectPk, this.type);
    const dataTransaction: EraPecEccAquaticDataTransaction = {pecEccAquatics: pecEccAquatics, rowsToDelete: this.pksRowsToDelete, geography: geographyName};

    this.logicService.save(dataTransaction).then(() => {
      this.saveRef === Constants.WIZARD_MENU_REF.Master ? this.menuService.setSuccess(true) : this.menuService.setSuccess(true);
    });

  }

  public onDelete(event: EraPecEccAquaticEu | EraPecEccAquaticUs): void {

    if( this.geography.geographyName === Constants.GEOGRAPHYS.EUROPE_UNION ){
      const { resultSurfaceWaterPk } = event as EraPecEccAquaticEu;
      this.setRowsToDelete(resultSurfaceWaterPk);
    }

    if( this.geography.geographyName === Constants.GEOGRAPHYS.USA ){
      const { resultsSummaryPK } = event as EraPecEccAquaticUs;
      this.setRowsToDelete(resultsSummaryPK);
    }

  }

  private setRowsToDelete(pk?: number): void{
    if(pk) {
      this.pksRowsToDelete.push(pk);
    }
  }

    public onSelectedRowEvent( event: RowSelectedEvent ){
      const node = event.node as any;
      node.data.eraSmeSelection = node.selected;
      node.data.eraManualSelection = true;
      this.grid.CreateTransaction( node.id, node.id, '', node.data);
    }

    public onBtnSelectAllClicked(): void {

      this.grid.gridApi.forEachNode((node: any) => {

        if( node.data.eraSmeSelection !== node.selected )
          node.data.eraSmeSelection = node.selected;

        node.data.eraManualSelection = true;
        this.grid.CreateTransaction( node.id, node.id, '', node.data);
      });
    }

    private refreshSelectedValuesTimeout(): void {
      setTimeout(() => {
        this.refreshSelectedValues();
      }, 75);
    }

    private refreshSelectedValues(): void {
      if( !this.grid ) return;
      this.grid.refreshSelectedValues( 'eraSmeSelection' );
    }

    public onAddNewRow(row: any): void {
      this.addTrasactionNewRowAquaticUs(row);
    }

    private addTrasactionNewRowAquaticUs( row: any ): void {
      if( this.type === Constants.PEC_ECC_TYPES.AQUATIC && this.geography.geographyName === Constants.GEOGRAPHYS.USA)
        this.grid.CreateTransaction('', '', '', row);
    }

    public onInputNumberChanged(event: any): void {
      this.CalculateWpezKgHa( event );
    }

    private CalculateWpezKgHa( event: any ): void{
      const { field, node } = event;
      if( field !== 'wpezlba' ) return;
      let data = node.data;
      let wpezkgha = Number(data.wpezlba) * ERAConstants.lbaToKgHa;
      wpezkgha = +wpezkgha.toFixed(6);
      data.wpezkgha = wpezkgha;
      this.grid.CreateTransaction(node.id, node.id, 'wpezkgha', data);
      this.grid.gridApi.redrawRows();
    }
}
